import { Alert, Box, Button, FormControl, TextField } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useAppSelector } from 'setup/store';
import UICard from 'components/lib/card/UICard';
import { IRole } from 'common/interfaces/irole';
import { formatDecimalNumber, validateFormData } from 'common/utils';
import { useNavigate } from 'react-router-dom';
import { useAppActions } from 'actions/app-actions';
import { ErrorType, FormItem } from 'common/interfaces';
import { IACHFormResponse } from 'common/interfaces/iRoyaltyReports';
import { useRoyaltyReportActions } from 'actions/royalty-report-actions';
import { useSessionActions } from 'actions/session-actions';
import { toNumber } from 'lodash';

import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';

type AchInputFormProps = {
  readonly?: boolean;
  formData?: IACHFormResponse;
};

const DEFAULT_FORM_DATA = {
  id: 0,
  storeId: 0,
  achReportId: 0,
  salesCash: 0,
  salesCashF1: 0,
  lotterySales: 0,
  lotterySalesF1: 0,
  lotterySalesF2: 0,
  lotteryBonus: 0,
  lotteryBonusF1: 0,
  lottoCommission: 0,
  lottoCommissionF1: 0,
  totalAmount: 0,
} as IACHFormResponse;

export const ACH_FORM_FIELDS = [
  {
    itemKey: 'storeName',
    label: 'Store',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'text',
    required: true,
    disabled: true,
  },
  {
    itemKey: 'salesCash',
    label: 'Sales',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'text',
    required: true,
  },
  {
    itemKey: 'salesCrvTaxable',
    label: 'CRV Taxable',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'decimal',
    required: true,
    disabled: true,
  },
  {
    itemKey: 'salesCrvNonTaxable',
    label: 'CRV Non Taxable',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'decimal',
    required: true,
    disabled: true,
  },
  {
    itemKey: 'salesCashWithoutCrv',
    label: 'Sales without CRV',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'decimal',
    required: true,
    disabled: true,
  },
  {
    itemKey: 'salesCashF1',
    label: 'Sales 5%',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'decimal',
    required: true,
    disabled: true,
  },
  {
    itemKey: 'lotterySales',
    label: 'Lottery Sales',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'decimal',
    required: true,
  },
  {
    itemKey: 'lotterySalesF1',
    label: 'Lottery Sales 6%',
    type: 'textInput',
    minLength: 1,
    maxLength: 5,
    validationType: 'decimal',
    required: true,
    disabled: true,
  },
  {
    itemKey: 'lotterySalesF2',
    label: 'Lottery Sales 5%',
    type: 'textInput',
    minLength: 1,
    maxLength: 5,
    validationType: 'decimal',
    required: true,
    disabled: true,
  },
  {
    itemKey: 'lotteryBonus',
    label: 'Lottery Bonus',
    type: 'textInput',
    minLength: 1,
    maxLength: 5,
    validationType: 'decimal',
    required: true,
  },
  {
    itemKey: 'lotteryBonusF1',
    label: 'Lottery Bonus 5%',
    type: 'textInput',
    minLength: 1,
    maxLength: 5,
    validationType: 'decimal',
    required: true,
    disabled: true,
  },
  {
    itemKey: 'lottoCommission',
    label: 'Lotto Commission',
    type: 'textInput',
    minLength: 1,
    maxLength: 5,
    validationType: 'decimal',
    required: true,
  },
  {
    itemKey: 'lottoCommissionF1',
    label: 'Lotto Commission 5%',
    type: 'textInput',
    minLength: 1,
    maxLength: 5,
    validationType: 'decimal',
    required: true,
    disabled: true,
  },
  {
    itemKey: 'totalAmount',
    label: 'Total Amount',
    type: 'textInput',
    minLength: 1,
    maxLength: 5,
    validationType: 'decimal',
    required: true,
    disabled: true,
  },
] as FormItem[];

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const AchInputForm: FC<AchInputFormProps> = ({ readonly, formData }) => {
  const roleIdMap = useAppSelector((state) => state.userReducer.roleIdMap);
  const roles = useAppSelector((state) => state.userReducer.roles);
  const error = useAppSelector((store) => store.royaltyReportReducer.error);
  const updateError = useAppSelector((store) => store.royaltyReportReducer.error);
  const updating = useAppSelector((store) => store.royaltyReportReducer.loading);
  const updatedFormData = useAppSelector((store) => store.royaltyReportReducer.updated);
  const loggedInUser = useAppSelector((store) => store.appReducer.loggedInUser);
  const royaltyConfig = useAppSelector((store) => store.sessionReducer.royaltyConfig);

  const navigate = useNavigate();
  const [formError, setFormError] = useState<ErrorType>({});
  const [_form, setForm] = useState<IACHFormResponse>();
  const [_roles, setRoles] = useState<IRole[]>([] as IRole[]);
  const [_readOnly, setReadOnly] = useState<boolean>(readonly || false);
  const { showSuccessSnackBar } = useAppActions();
  const { loadRoyaltyConfigs } = useSessionActions();
  const { updateACHFormResponse } = useRoyaltyReportActions();

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const resetFormData = (formData?: IACHFormResponse) => {
    setForm(formData || DEFAULT_FORM_DATA);
  };
  useEffect(() => {
    resetFormData(formData);
    if (!royaltyConfig) {
      loadRoyaltyConfigs();
    }
  }, [formData]);

  const validateForm = (itemKey?: string, value?: any) => {
    const validation = validateFormData(ACH_FORM_FIELDS, _form, itemKey, value);
    setFormError({ ...validation.error });
    return validation.valid;
  };

  const _handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (_form) {
      const isValid = validateForm();
      if (isValid) {
        const resp = await updateACHFormResponse(_form);
        console.log('_handleFormSubmit:', resp);
        if (resp) {
          showSuccessSnackBar('Successfully updated form for store number ' + _form.storeName + '.');
        }
      } else {
        setOpen(true);
      }
    }
  };

  const _handleCancel = () => {
    resetFormData(formData);
  };

  const _handleInputChange = async (key: string, value: any) => {
    const tempData = { ..._form } as IACHFormResponse;
    (tempData as any)[key] = value;

    if (royaltyConfig) {
      switch (key) {
        case 'salesCash':
          const salesCashWithoutCrv =
            toNumber(value) - (tempData.salesCrvNonTaxable || 0) - (tempData.salesCrvTaxable || 0);
          const salesF1 = toNumber(salesCashWithoutCrv) * (royaltyConfig.salesCashF1 / 100);
          tempData.salesCashF1 = formatDecimalNumber(salesF1);
          tempData.salesCashWithoutCrv = salesCashWithoutCrv;
          break;
        case 'lotterySales':
          const lotterySalesF1 = toNumber(value) * (royaltyConfig.lotterySalesF1 / 100);
          tempData.lotterySalesF1 = formatDecimalNumber(lotterySalesF1);
          const lotterySalesF2 = tempData.lotterySalesF1 * (royaltyConfig.lotterySalesF2 / 100);
          tempData.lotterySalesF2 = formatDecimalNumber(lotterySalesF2);
          break;
        case 'lotteryBonus':
          const lotteryBonusF1 = toNumber(value) * (royaltyConfig.lotteryBonusF1 / 100);
          tempData.lotteryBonusF1 = formatDecimalNumber(lotteryBonusF1);
          break;
        case 'lottoCommission':
          const lottoCommissionF1 = toNumber(value) * (royaltyConfig.lottoCommissionF1 / 100);
          tempData.lottoCommissionF1 = formatDecimalNumber(lottoCommissionF1);
          break;
      }
    }
    tempData.totalAmount = formatDecimalNumber(
      tempData.salesCashF1 + tempData.lotterySalesF2 + tempData.lotteryBonusF1 + tempData.lottoCommissionF1,
    );
    setForm(tempData);
    validateForm();
  };
  return (
    <div>
      {error ? <Alert severity="error"> {error.msg}</Alert> : null}
      <Box
        component="form"
        // sx={{
        //   '& .MuiTextField-root': { m: 1.5, width: '50ch' },
        //   '& .select': { m: 1.5, width: '50ch' },
        //   '& .selectLabel': { m: 1.5 },
        //   '& .MuiButton-root': { m: 1.5 },
        //   //   '& .MuiSelect-root': { m: 1, width: '30ch' },
        // }}
        noValidate
        autoComplete="off"
      >
        {_form ? (
          <div>
            {/* <UICard title="#101"> */}
            <Box display="flex" flexDirection="row" gap={1}>
              {ACH_FORM_FIELDS.map((fitem, index) => (
                // <FormControl >
                <TextField
                  key={'' + fitem.itemKey + ':' + index}
                  disabled={_readOnly || fitem.disabled}
                  required
                  id={'txtAchInput-' + fitem.itemKey + ':' + index}
                  label={fitem.label}
                  value={(_form as any)[fitem.itemKey]}
                  error={formError[fitem.itemKey] ? true : false}
                  // helperText={formError[fitem.itemKey] ? formError[fitem.itemKey] : ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    _handleInputChange(fitem.itemKey, event.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '12px', // Default label font size when nothing is entered
                    },
                    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                      fontSize: '14px', // Smaller font size when the label is "shrunk" (focused or filled)
                    },
                  }}
                  style={{ width: 140, height: 20, display: fitem.itemKey == 'storeName' ? 'none' : 'flex' }}
                  size="small"
                />
                // </FormControl>
              ))}

              {updateError ? <Alert severity="error">{updateError?.msg}</Alert> : null}
              {/* {isUpdated ? <Alert severity="success">{'Updated successfully!'}</Alert> : null} */}
              <Button
                disabled={_readOnly}
                variant="contained"
                onClick={_handleFormSubmit}
                // style={{ marginTop: '30px' }}
              >
                Submit
              </Button>
              <Button
                disabled={updating}
                variant="contained"
                onClick={_handleCancel}
                // style={{ marginTop: '30px' }}
                color="error"
              >
                Cancel
              </Button>
            </Box>
            {/* </UICard> */}
          </div>
        ) : null}
      </Box>
      <BootstrapDialog onClose={handleClose} aria-labelledby="error-dialog-title" open={open} fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }} id="error-dialog-title">
          Errors!
          <Typography marginTop={2}>Please correct the errors to continue.</Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          {formError
            ? Object.keys(formError).map((key, index) => (
                <Typography gutterBottom color="red">
                  {index + 1}. {formError[key]}
                </Typography>
              ))
            : null}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </div>
  );
};

export default AchInputForm;
